/**
 * The sidebar's width, its collapsed state, and the two controls that change
 * them.
 *
 * **Collapsed means gone, not narrowed.** The usual rail — a 3rem strip of
 * icons — is not available here: §7.2's navigation is seven words, and none of
 * them has an icon that would survive losing its label. "Effective access" and
 * "Organizations" do not reduce to glyphs a person could tell apart, so a rail
 * would be a row of shapes that have to be hovered to be read.
 *
 * **The toggle travels with the thing it controls.** It sits in the sidebar's
 * own header while there is a sidebar to sit in, and reappears in the shell bar
 * once there is not — one button, in whichever of the two places is currently
 * on screen.
 *
 * The `<aside>` stays mounted and collapses by width rather than unmounting.
 * That is what keeps `aria-controls` pointing at something that exists, what
 * lets the collapse animate at all, and what makes expanding restore scroll
 * position and query state rather than remounting the tree.
 *
 * Both shells share one stored preference for both values: somebody who set the
 * sidebar's width on the employee surface did not ask for a different one the
 * moment they open Schema.
 */

import { type KeyboardEvent, type PointerEvent, useCallback, useEffect, useState } from 'react';
import { Button } from '../components/ui/button.tsx';
import { cn } from '../components/ui/cn.ts';

const COLLAPSED_KEY = 'mb.sidebar';
const WIDTH_KEY = 'mb.sidebar.width';

const DEFAULT_WIDTH = 256;
const MIN_WIDTH = 200;
const MAX_WIDTH = 420;

/**
 * Drag the edge left of this and the sidebar closes rather than getting
 * narrower. Below `MIN_WIDTH` the navigation starts truncating its own labels,
 * and a panel too narrow to read is not a smaller sidebar — it is a closed one
 * that still takes up room. The gap between the two numbers is deliberate:
 * close enough to reach on purpose, far enough not to trip over.
 */
const COLLAPSE_AT = 140;

/** The `id` on the `<aside>`, and what both controls' `aria-controls` names. */
export const SIDEBAR_ID = 'shell-sidebar';

function storedWidth(): number {
  const raw = Number.parseInt(localStorage.getItem(WIDTH_KEY) ?? '', 10);
  // A width from storage is attacker-free but not garbage-free: a half-written
  // value, or one saved before the bounds moved, should land on the default
  // rather than on a 3px sidebar nobody can grab the edge of.
  return Number.isSafeInteger(raw) && raw >= MIN_WIDTH && raw <= MAX_WIDTH ? raw : DEFAULT_WIDTH;
}

export interface Sidebar {
  collapsed: boolean;
  width: number;
  /** True only between pointerdown and pointerup on the resize handle. */
  dragging: boolean;
  toggle: () => void;
  startResize: (event: PointerEvent<HTMLElement>) => void;
  resizeByKey: (event: KeyboardEvent<HTMLElement>) => void;
}

export function useSidebar(): Sidebar {
  const [collapsed, setCollapsed] = useState<boolean>(
    () => localStorage.getItem(COLLAPSED_KEY) === 'collapsed',
  );
  const [width, setWidth] = useState<number>(storedWidth);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? 'collapsed' : 'expanded');
  }, [collapsed]);

  useEffect(() => {
    localStorage.setItem(WIDTH_KEY, String(width));
  }, [width]);

  const toggle = useCallback(() => setCollapsed((current) => !current), []);

  /**
   * The listeners go on `window` rather than on the handle under
   * `setPointerCapture`. Capture is the tidier mechanism and it is also the one
   * that can refuse: it throws when the pointer it names is no longer active,
   * which is a real race on a fast click-drag and would abort the handler
   * *before* the listeners were attached — a drag that silently does nothing.
   * `window` cannot refuse, and the thing being dragged is a 4px line the
   * pointer leaves on the first frame regardless.
   *
   * `clientX` *is* the width, with no offset arithmetic, because the sidebar
   * starts at the left edge of the window. If it ever stops doing so this
   * breaks visibly and immediately rather than drifting.
   */
  const startResize = useCallback((event: PointerEvent<HTMLElement>) => {
    // Stops the drag from starting a text selection across the whole shell.
    event.preventDefault();
    setDragging(true);

    const finish = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
    };

    function move(moved: globalThis.PointerEvent) {
      if (moved.clientX < COLLAPSE_AT) {
        setCollapsed(true);
        finish();
        return;
      }
      setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(moved.clientX))));
    }

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  }, []);

  /** The window-splitter keys: arrows nudge, Home/End go to the stops, Enter closes. */
  const resizeByKey = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const step = event.shiftKey ? 48 : 16;
      const clamp = (next: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, next));

      switch (event.key) {
        case 'ArrowLeft':
          setWidth((current) => clamp(current - step));
          break;
        case 'ArrowRight':
          setWidth((current) => clamp(current + step));
          break;
        case 'Home':
          setWidth(MIN_WIDTH);
          break;
        case 'End':
          setWidth(MAX_WIDTH);
          break;
        case 'Enter':
        case ' ':
          toggle();
          break;
        default:
          return;
      }
      event.preventDefault();
    },
    [toggle],
  );

  return { collapsed, width, dragging, toggle, startResize, resizeByKey };
}

/**
 * The classes the `<aside>` collapses with, and the custom property its width
 * is read from.
 *
 * Width rather than `display: none`, because `display` does not interpolate and
 * a sidebar that vanishes between frames reads as a layout bug rather than as a
 * thing that closed. The panel is clipped by `overflow-hidden` and keeps its
 * full width on an inner element, so it slides out of view instead of reflowing
 * its own contents down to nothing on the way.
 *
 * `visibility` is in the transition list on purpose. It interpolates
 * discretely — staying `visible` for the whole collapse and flipping at the
 * end — which is exactly the timing wanted: the panel is legible while it
 * leaves, and once it is gone it is out of the tab order and out of the
 * accessibility tree rather than a 0px-wide element a keyboard can still reach.
 *
 * The transition comes off while a drag is in flight. A 200ms ease on a value
 * being set from `pointermove` is a sidebar that trails the cursor and settles
 * a fifth of a second after it stops.
 *
 * Below `lg` the sidebar is a stacked block rather than a column: it is not
 * resizable there, and height animates from `auto` in no browser worth the
 * workaround, so it opens and closes at once.
 */
export function sidebarClasses(sidebar: Pick<Sidebar, 'collapsed' | 'dragging'>): string {
  return cn(
    'shrink-0 overflow-hidden border-secondary bg-secondary',
    'lg:sticky lg:top-0 lg:h-screen',
    !sidebar.dragging && 'transition-[width,height,visibility] duration-200 ease-out',
    sidebar.collapsed
      ? 'invisible h-0 border-b-0 lg:h-screen lg:w-0 lg:border-r-0'
      : 'visible border-b lg:w-[var(--sidebar-width)] lg:border-r lg:border-b-0',
  );
}

/** For the `style` prop on the `<aside>`; the inner panel inherits it. */
export function sidebarStyle(sidebar: Pick<Sidebar, 'width'>): Record<string, string> {
  return { '--sidebar-width': `${sidebar.width}px` };
}

/**
 * The draggable edge.
 *
 * `role="separator"` with a `tabindex` is the window-splitter pattern: it is
 * the one widget whose whole job is to sit between two regions and move, and
 * `aria-valuenow` is what makes the arrow keys mean something to somebody who
 * cannot see the sidebar move.
 *
 * It takes no space in the layout — `w-1` cancelled by `-ml-1` — so it overlays
 * the sidebar's own right border rather than adding a second line beside it,
 * and its hit area is widened past its paint area by the pseudo-element, which
 * is the difference between a control you can grab and one you have to aim at.
 */
export function SidebarResizeHandle({ sidebar }: { sidebar: Sidebar }) {
  if (sidebar.collapsed) return null;

  return (
    /* An `<hr>` rather than a `<div role="separator">`: a thematic break
       between two regions is what the element means, and it is what the
       markup already has to claim for the arrow keys to be announced. Its
       own margin and rule are reset — the paint here is the pseudo-element. */
    <hr
      aria-orientation="vertical"
      aria-label="Resize sidebar"
      aria-controls={SIDEBAR_ID}
      aria-valuenow={sidebar.width}
      aria-valuemin={MIN_WIDTH}
      aria-valuemax={MAX_WIDTH}
      tabIndex={0}
      onPointerDown={sidebar.startResize}
      onKeyDown={sidebar.resizeByKey}
      className={cn(
        'relative z-20 -ml-1 m-0 hidden w-1 shrink-0 cursor-col-resize border-0 outline-none lg:block',
        'after:absolute after:inset-y-0 after:-left-1 after:-right-1 after:transition-colors',
        'hover:after:bg-brand-solid/20 focus-visible:after:bg-brand-solid/40',
        sidebar.dragging && 'after:bg-brand-solid/30',
      )}
    />
  );
}

/**
 * The toggle's glyph, hand-written rather than `@untitledui/icons`' `LayoutLeft`.
 *
 * The library's icon is one `<path>` — frame and rail in a single `d` — and
 * this needs the two to move independently: on hover the rail slides toward the
 * edge it is docked against and the panel behind it fills, which is the whole
 * of the affordance. The geometry is the library's, so it sits correctly beside
 * the other icons in the shell.
 *
 * Mirroring the whole glyph when collapsed carries the hover with it: the rail
 * always moves toward its own side, so the animation reads as "this panel is
 * about to move" in both directions without a second rule.
 */
function SidebarGlyph({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={cn('transition-transform duration-200 ease-out', collapsed && '-scale-x-100')}
    >
      <path
        d="M5 4h4v16H5z"
        fill="currentColor"
        className="opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-20"
      />
      <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" strokeWidth={2} />
      <path
        d="M9 3v18"
        stroke="currentColor"
        strokeWidth={2}
        className="transition-transform duration-200 ease-out group-hover:-translate-x-px"
      />
    </svg>
  );
}

export function SidebarToggle({ sidebar }: { sidebar: Pick<Sidebar, 'collapsed' | 'toggle'> }) {
  return (
    <Button
      variant="tertiary"
      size="sm"
      className="group size-8 shrink-0 p-0"
      aria-label={sidebar.collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!sidebar.collapsed}
      aria-controls={SIDEBAR_ID}
      onPress={sidebar.toggle}
    >
      <SidebarGlyph collapsed={sidebar.collapsed} />
    </Button>
  );
}
